import { supabase } from '../../config/supabaseClient';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface Attachment {
  id: string;
  task_id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  uploaded_by: string;
  created_at: string;
  // From join
  uploader_name?: string;
  uploader_email?: string;
}

// =====================================================
// SERVICE IMPLEMENTATION
// =====================================================

const STORAGE_BUCKET = 'pm-attachments';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

async function getTaskOrganizationId(taskId: string): Promise<string> {
  const { data, error } = await supabase
    .from('pm_tasks')
    .select('organization_id')
    .eq('id', taskId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  const orgId = (data as any)?.organization_id as string | undefined;
  if (!orgId) {
    throw new Error('Task not found');
  }
  return orgId;
}

function mapRowToAttachment(row: any, taskId: string): Attachment {
  return {
    id: row.id,
    task_id: taskId,
    file_name: row.name,
    file_size: Number(row.file_size || 0),
    file_type: String(row.file_type || 'application/octet-stream'),
    storage_path: String(row.file_url || ''),
    uploaded_by: row.uploaded_by,
    created_at: row.created_at,
    uploader_email: row.users?.email,
    uploader_name: row.users?.raw_user_meta_data?.full_name,
  };
}

export const attachmentsService = {
  /**
   * List attachments for a task
   */
  async list(taskId: string): Promise<{ data: Attachment[] | null; error: any }> {
    try {
      const organizationId = await getTaskOrganizationId(taskId);
      const { data, error } = await supabase
        .from('pm_attachments')
        .select(`
          *,
          users:uploaded_by (
            email,
            raw_user_meta_data
          )
        `)
        .eq('organization_id', organizationId)
        .eq('attached_to_type', 'task')
        .eq('attached_to_id', taskId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching attachments:', error);
        return { data: null, error: error.message };
      }

      const formatted = (data || []).map((row: any) => mapRowToAttachment(row, taskId));

      return { data: formatted, error: null };
    } catch (err: any) {
      console.error('Error in attachmentsService.list:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Upload a file attachment
   */
  async upload(
    taskId: string,
    file: File
  ): Promise<{ data: Attachment | null; error: any }> {
    try {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        return { data: null, error: 'File size exceeds 10MB limit' };
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      const organizationId = await getTaskOrganizationId(taskId);

      // Generate unique file path
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${user.id}/${taskId}/${timestamp}_${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        return { data: null, error: uploadError.message };
      }

      // Create database record
      const { data: attachment, error: dbError } = await supabase
        .from('pm_attachments')
        .insert({
          organization_id: organizationId,
          attached_to_type: 'task',
          attached_to_id: taskId,
          name: file.name,
          file_url: storagePath,
          file_type: file.type || 'application/octet-stream',
          file_size: file.size,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (dbError) {
        console.error('Error creating attachment record:', dbError);
        // Clean up uploaded file
        await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
        return { data: null, error: dbError.message };
      }

      return { data: attachment ? mapRowToAttachment(attachment, taskId) : null, error: null };
    } catch (err: any) {
      console.error('Error in attachmentsService.upload:', err);
      return { data: null, error: err.message || 'Upload failed' };
    }
  },

  /**
   * Download/get URL for an attachment
   */
  async getDownloadUrl(storagePath: string): Promise<{ data: string | null; error: any }> {
    try {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error getting download URL:', error);
        return { data: null, error: error.message };
      }

      return { data: data.signedUrl, error: null };
    } catch (err: any) {
      console.error('Error in attachmentsService.getDownloadUrl:', err);
      return { data: null, error: err.message || 'Failed to get download URL' };
    }
  },

  /**
   * Delete an attachment
   */
  async delete(attachmentId: string): Promise<{ error: any }> {
    try {
      // Get attachment info
      const { data: attachment, error: fetchError } = await supabase
        .from('pm_attachments')
        .select('file_url, uploaded_by')
        .eq('id', attachmentId)
        .single();

      if (fetchError || !attachment) {
        return { error: 'Attachment not found' };
      }

      // Verify ownership
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || attachment.uploaded_by !== user.id) {
        return { error: 'Not authorized to delete this attachment' };
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([attachment.file_url]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Continue to delete DB record anyway
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('pm_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) {
        console.error('Error deleting attachment record:', dbError);
        return { error: dbError.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in attachmentsService.delete:', err);
      return { error: err.message || 'Failed to delete attachment' };
    }
  },

  /**
   * Get file icon based on file type
   */
  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.includes('zip') || fileType.includes('archive')) return '📦';
    if (fileType.includes('text')) return '📃';
    return '📎';
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  },
};
